import { useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import OffersSection from './components/OffersSection';
import ProductsByCategorySection from './components/ProductsByCategorySection';
import ServicesSection from './components/ServicesSection';
import SeoFaqSection from './components/SeoFaqSection';
import ScrollReveal from '@/components/utils/ScrollReveal';
import TestimonialsSection from './components/TestimonialsSection';
import BrandsSection from '@/components/feature/BrandsSection';
import { useSeo } from '@/seo/useSeo';
import { seoInicio } from '@/seo/datos';

function scrollToSection(id: string, retries = 10) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else if (retries > 0) {
    setTimeout(() => scrollToSection(id, retries - 1), 200);
  }
}

const SCROLL_SECTIONS = ['servicios'];

export default function Home() {
  useSeo(seoInicio());
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Handle ?section= from navigation (e.g. clicking Servicios from another page)
  useEffect(() => {
    const section = searchParams.get('section');
    if (!section) return;
    setSearchParams({}, { replace: true });
    setTimeout(() => {
      scrollToSection(section);
      window.history.replaceState(null, '', `#${section}`);
    }, 300);
  }, [searchParams, setSearchParams]);

  // Handle direct hash on load (e.g. user visits /#servicios)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && SCROLL_SECTIONS.includes(hash)) {
      setTimeout(() => scrollToSection(hash), 400);
    }
  }, []);

  // Update URL hash as user scrolls through sections
  useEffect(() => {
    const sections = SCROLL_SECTIONS.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (sections.length === 0) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            const id = entry.target.id;
            const currentHash = window.location.hash.replace('#', '');
            if (currentHash !== id) {
              window.history.replaceState(null, '', `#${id}`);
            }
          }
        });
      },
      { threshold: [0.3, 0.5, 0.7], rootMargin: '-80px 0px -40% 0px' }
    );

    sections.forEach((section) => observerRef.current?.observe(section));

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return (
    <>
      <Navbar />
      <main id="main-content" tabIndex={-1}>
        {/* Hero — sin animación, ya tiene la suya propia */}
        <HeroSection />

        {/* Ofertas — zoom-up para que llame la atención */}
        <ScrollReveal animation="zoom-up" duration={600} threshold={0.06}>
          <OffersSection />
        </ScrollReveal>

        {/* Productos por categoría — fade up */}
        <ScrollReveal animation="fade-up" duration={700} threshold={0.06}>
          <ProductsByCategorySection />
        </ScrollReveal>

        {/* Servicios — fade desde la izquierda */}
        <ScrollReveal animation="fade-right" duration={650} threshold={0.08}>
          <ServicesSection />
        </ScrollReveal>

        {/* FAQ SEO — fade up con ligero delay */}
        <ScrollReveal animation="fade-up" duration={650} delay={80} threshold={0.08}>
          <SeoFaqSection />
        </ScrollReveal>

        {/**
         * ═══════════════════════════════════════════════════════════════
         *  SECCIÓN: Opiniones reales (Testimonios)
         * ═══════════════════════════════════════════════════════════════
         *  Módulo que muestra reseñas de clientes verificados.
         *  Incluye:
         *  - 6 testimonios con foto, nombre, ubicación, producto comprado
         *  - Sistema de estrellas y badge "compra verificada"
         *  - Rating promedio y gráfico de distribución por estrellas
         *  - Layout: grid 3 cols en desktop, carrusel horizontal en mobile
         *
         *  ARCHIVO: src/pages/home/components/TestimonialsSection.tsx
         *
         *  PARA ACTIVAR: Descomenta el bloque de abajo y quita esta
         *  documentación si quieres que se muestre en la página.
         * ═══════════════════════════════════════════════════════════════
         */}
        {/*
        <ScrollReveal animation="fade-up" duration={650} threshold={0.06}>
          <TestimonialsSection />
        </ScrollReveal>
        */}

        {/* Marcas — fade up */}
        <ScrollReveal animation="fade-up" duration={600} threshold={0.06}>
          <BrandsSection
            theme="light"
            title="Cartera de marcas"
            subtitle="Trabajamos con los fabricantes más reconocidos del mercado solar mundial"
          />
        </ScrollReveal>
      </main>
      <Footer />
    </>
  );
}
